/**
 * 체험단 신청 캠페인 설정
 */
import RefundBadge from "../components/RefundBadge";

const formatBusinessHours = (hours) => {
  if (!hours) return '-';

  // 이미 객체인 경우 (API 응답)
  if (typeof hours === 'object' && hours !== null) {
    const parts = [];

    // weekDay 처리
    if (hours.weekDay && hours.weekDay.startTime && hours.weekDay.endTime) {
      const days = hours.weekDay.possibleDay?.join(', ') || '평일';
      parts.push(`${days}: ${hours.weekDay.startTime} ~ ${hours.weekDay.endTime}`);
    }

    // weekend 처리
    if (hours.weekend && hours.weekend.startTime && hours.weekend.endTime) {
      const days = hours.weekend.possibleDay?.join(', ') || '주말';
      parts.push(`${days}: ${hours.weekend.startTime} ~ ${hours.weekend.endTime}`);
    }

    return parts.length > 0 ? parts.join(' / ') : '-';
  }

  // 문자열인 경우 JSON 파싱 시도
  if (typeof hours === 'string') {
    try {
      const parsed = JSON.parse(hours);
      if (Array.isArray(parsed)) {
        return parsed.map(h => `${h.day}: ${h.time}`).join(', ');
      }
      // 재귀적으로 객체 처리
      return formatBusinessHours(parsed);
    } catch (e) {
      return hours;
    }
  }

  return String(hours);
};


export default {
  id: "adExperienceReviewer",
  title: "체험단 신청",

  api: {
    list: {
      method: "GET",
      url: "/advertisement/admin/experienceReviewers",
    },
    count: {
      method: "GET",
      url: "/advertisement/admin/experienceReviewers/count",
    },
    submitted: {
      method: "POST",
      url: "/advertisement/admin/experienceReviewers/submitted",
    },
    paid: {
      method: "POST",
      url: "/advertisement/admin/experienceReviewers/paid",
    },
    complete: {
      method: "POST",
      url: "/advertisement/admin/experienceReviewers/complete",
    },
    refund: {
      method: "POST",
      url: "/advertisement/admin/campaigns/refund",
      version: 2,
    },
    delete: {
      method: "DELETE",
      url: "/advertisement/admin/experienceReviewers",
    },
  },

  filters: {
    default: {
      status: "전체",
      submitDate: "",
      keyword: "",
      page: 1,
      limit: 50,
      submitted: "전체",
      paid: "전체",
      productTypes: [],
    },
    schema: [
      {
        name: "productTypes",
        label: "상품 타입",
        type: "checkbox-group",
        options: [
          { value: "onsite_blog_review", label: "방문형 블로그" },
          { value: "xiaohongshu", label: "샤오홍슈" },
          { value: "japanese", label: "일본인 체험단" },
        ],
      },
      {
        name: "keyword",
        label: "검색",
        type: "text",
        placeholder: "신청자, 플레이스명",
      },
      {
        name: "submitDate",
        label: "접수일",
        type: "date",
      },
      {
        name: "submitted",
        label: "접수여부",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "complete", label: "접수완료" },
          { value: "notComplete", label: "미접수" },
        ],
      },
      {
        name: "paid",
        label: "결제상태",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "complete", label: "결제완료" },
          { value: "notComplete", label: "미결제" },
          { value: "refund", label: "환불" },
        ],
      },
      {
        name: "status",
        label: "상태",
        type: "select",
        options: [
          { value: "전체", label: "전체" },
          { value: "waiting", label: "대기중" },
          { value: "processing", label: "진행중" },
          { value: "completed", label: "종료" },
        ],
      },
    ],
  },

  summary: {
    fields: [
      { key: "totalCount", label: "총 신청" },
      { key: "waitingCount", label: "대기중" },
      { key: "processingCount", label: "진행중" },
      { key: "completedCount", label: "종료" },
    ],
  },

  table: {
    keyField: "adExperienceReviewerId",
    columns: [
      {
        key: "adCampaignMetaId",
        label: "아이디",
        center: true,
      },
      {
        key: "createdAt",
        label: "캐랭접수시간",
        center: true,
        render: (row) => {
          if (!row.createdAt) return "";
          const [date, time] = row.createdAt.split(" ");
          return (
            <>
              <div>{date}</div>
              <div>{time?.slice(0, 8)}</div>
            </>
          );
        },
      },
      {
        key: "userName",
        label: "신청자(ID)\n상태",
        center: true,
        render: (row) => (
          <>
            <div>
              {row.userName}
              {row.userId ? ` (${row.userId})` : ""}
            </div>
            <div>
              <span className={`badge badge-${row.status}`}>{getStatusLabel(row.status)}</span>
            </div>
          </>
        ),
      },
      {
        key: "submitDate",
        label: "접수일",
        center: true,
        render: (row) => (row.submitDate ? row.submitDate.split("T")[0] : ""),
      },
      {
        key: "type",
        label: "상품타입\n총수량\n총금액(단가)",
        center: true,
        render: (row) => {
          const productTypes = [
            { value: "onsite_blog_review", label: "방문형 블로그" },
            { value: "xiaohongshu", label: "샤오홍슈" },
            { value: "japanese", label: "일본인 체험단" },
          ];
          const getProductTypeLabel = (type) => {
            const productType = productTypes.find((pt) => pt.value === type);
            return productType ? productType.label : type;
          };

          return (
            <>
              <div>{getProductTypeLabel(row.type)}</div>
              <div>{row.totalQuantity}건</div>
              <div>
                {row.totalAmount?.toLocaleString()}원 (
                {Math.floor(row.totalAmount / row.totalQuantity)?.toLocaleString()}원)
              </div>
            </>
          );
        },
      },
      {
        key: "placeName",
        label: "플레이스명\n연락처\n플레이스 URL",
        center: true,
        render: (row) => {
          const removeKoreanAndSpaces = (url) => {
            if (!url) return "";
            const httpsIndex = url.indexOf("https://");
            if (httpsIndex !== -1) {
              url = url.substring(httpsIndex);
            }
            return url.replace(/[\uAC00-\uD7A3\s]/g, "");
          };

          return (
            <>
              <div>{row.placeName}</div>
              <div>{row.reservationPhoneNum || ""}</div>
              <div className="place-url-container">
                {row.placeUrl && (
                  <a
                    href={removeKoreanAndSpaces(row.placeUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="place-url-link"
                  >
                    {removeKoreanAndSpaces(row.placeUrl)}
                  </a>
                )}
              </div>
            </>
          );
        },
      },
      {
        key: "productName",
        label: "제공 상품명\n제공 상품 가격\n방문일",
        center: true,
        render: (row) => (
          <>
            <div>{row.productName}</div>
            <div>{row.productPrice?.toLocaleString()}원</div>
            <div>{row.visitDate || ""}</div>
          </>
        ),
      },
      {
        key: "businessHours",
        label: "영업시간",
        center: true,
        render: (row) => <div>{formatBusinessHours(row.businessHours)}</div>,
      },
      {
        key: "request",
        label: "요청사항",
        center: true,
        render: (row) => (
          <div className="guideline-textbox" data-reviewer-id={row.adExperienceReviewerId} title={row.request || "-"}>
            {row.request || "-"}
          </div>
        ),
      },
      {
        key: "paymentType",
        label: "결제방법\n결제상태",
        center: true,
        render: (row) => (
          <>
            <div>{row.paymentType === "card" ? "카드" : row.paymentType === "v_bank" ? "가상계좌" : row.paymentType === "point" ? "포인트 결제" : "계좌이체"}</div>
            <div>
              {row.isPaid === 2 ? (
                <RefundBadge
                  refundedAmount={row.refundedAmount ?? 0}
                  remainingAmount={row.remainingAmount ?? 0}
                  refundDate={row.refundDate}
                />
              ) : (
                <span className={`badge badge-${row.isPaid === 1 ? "결제완료" : "미결제"}`}>
                  {row.isPaid === 1 ? "결제완료" : "미결제"}
                </span>
              )}
            </div>
          </>
        ),
      },
      {
        key: "report",
        label: "리포트",
        center: true,
        render: (row) => {
          // xiaohongshu 타입일 때만 리포트 버튼 표시
          if (row.type === "xiaohongshu" || row.type === "japanese") {
            return (
              <button
                type="button"
                className="status-btn submitted"
                data-campaign-meta-id={row.adCampaignMetaId}
                data-type={row.type}
                data-place-name={row.placeName || ""}
              >
                리포트
              </button>
            );
          }
          return null;
        },
      },
      {
        key: "notification",
        label: "알림톡",
        center: true,
        render: (row) => {
          const productTypes = [
            { value: "onsite_blog_review", label: "방문형 블로그" },
            { value: "xiaohongshu", label: "샤오홍슈" },
            { value: "japanese", label: "일본인 체험단" },
          ];
          const getProductTypeLabel = (type) => {
            const productType = productTypes.find((pt) => pt.value === type);
            return productType ? productType.label : type;
          };
          const productName = getProductTypeLabel(row.type) || "체험단 신청";

          return (
            <button
              type="button"
              className="notification-btn"
              data-campaign-meta-id={row.adCampaignMetaId}
              data-action="notification"
              data-user-id={row.userId}
              data-product-name={productName}
              data-submitted-at={row.createdAt || ""}
              data-purchase-amount={row.totalAmount ?? ""}
              onClick={(e) => e.stopPropagation()}
            >
              알림톡
            </button>
          );
        },
      },
      {
        key: "memo",
        label: "메모",
        center: true,
        render: (row) => (
          <button
            type="button"
            className={row.memo ? "memo-btn has-memo" : "memo-btn"}
            data-action="memo"
            data-campaign-meta-id={row.adCampaignMetaId}
            data-memo={row.memo || ""}
            onClick={(e) => e.stopPropagation()}
          >
            {row.memo
              ? <span className="memo-preview">{row.memo}</span>
              : "메모"}
          </button>
        ),
      },
    ],
  },

  actions: [
    {
      id: "submitted",
      label: "접수/취소",
      actionType: "submitted",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      payloadBuilder: (selectedIds, items) => {
        const hasUnsubmitted = items.some(
          (item) => selectedIds.includes(item.adExperienceReviewerId) && item.isSubmitted !== 1,
        );
        return {
          adExperienceReviewerIds: selectedIds,
          toggleValue: hasUnsubmitted ? 1 : 0,
        };
      },
    },
    {
      id: "paid",
      label: "결제완료",
      actionType: "paid",
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        const alreadyPaid = items.some(
          (item) => selectedIds.includes(item.adExperienceReviewerId) && (item.isPaid === true || item.isPaid === 1),
        );
        return !alreadyPaid;
      },
      payloadBuilder: (selectedIds) => ({
        adExperienceReviewerIds: selectedIds,
        toggleValue: 1,
      }),
    },
    {
      id: "complete",
      label: "처리완료/취소",
      actionType: "complete",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      payloadBuilder: (selectedIds, items) => {
        const hasNotCompleted = items.some(
          (item) => selectedIds.includes(item.adExperienceReviewerId) && item.status !== "completed",
        );
        return {
          adExperienceReviewerIds: selectedIds,
          toggleValue: hasNotCompleted ? 1 : 0,
        };
      },
    },
    {
      id: "refund",
      label: "환불/취소",
      actionType: "refund",
      requires: "ad.campaign.refund", // 권한 없으면 버튼 숨김 (R-FE-04)
      enabledWhen: (selectedIds, items) => {
        if (selectedIds.length === 0) return false;
        return items.some(
          (item) => selectedIds.includes(item.adExperienceReviewerId) && (item.isPaid === 1 || item.isPaid === 2) && (item.remainingAmount ?? item.totalAmount ?? 0) > 0
        );
      },
    },
    {
      id: "delete",
      label: "삭제",
      actionType: "delete",
      enabledWhen: (selectedIds) => selectedIds.length > 0,
      confirm: "선택한 신청을 삭제하시겠습니까?",
      payloadBuilder: (selectedIds) => ({
        adExperienceReviewerIds: selectedIds,
      }),
    },
  ],

  download: {
    type: "xlsx",
    fileName: "체험단_신청목록.xlsx",
    sheetName: "체험단신청목록",
    transformRow: (row) => {
      const productTypes = [
        { value: "onsite_blog_review", label: "방문형 블로그" },
        { value: "xiaohongshu", label: "샤오홍슈" },
        { value: "japanese", label: "일본인 체험단" },
      ];
      const getProductTypeLabel = (type) => {
        const productType = productTypes.find((pt) => pt.value === type);
        return productType ? productType.label : type;
      };

      const removeKoreanAndSpaces = (url) => {
        if (!url) return "";
        const httpsIndex = url.indexOf("https://");
        if (httpsIndex !== -1) {
          url = url.substring(httpsIndex);
        }
        return url.replace(/[\uAC00-\uD7A3\s]/g, "");
      };

      return {
        신청자: row.userName || "",
        접수일: row.submitDate ? row.submitDate.split("T")[0] : "",
        상품타입: getProductTypeLabel(row.type),
        총수량: row.totalQuantity || 0,
        총금액: row.totalAmount || 0,
        단가: Math.floor(row.totalAmount / row.totalQuantity) || 0,
        플레이스명: row.placeName || "",
        연락처: row.reservationPhoneNum || "",
        플레이스URL: removeKoreanAndSpaces(row.placeUrl) || "",
        제공상품명: row.productName || "",
        제공상품가격: row.productPrice || 0,
        방문일: row.visitDate || "",
        영업시간: formatBusinessHours(row.businessHours),
        요청사항: row.request || "",
      };
    },
  },
};

function getStatusLabel(status) {
  switch (status) {
    case "waiting":
      return "대기중";
    case "processing":
      return "진행중";
    case "completed":
      return "종료";
    default:
      return status;
  }
}
